/* exchangeReadiness.js — Deterministic operational readiness engine
 *
 * Pure function. No AI, no ML, no prediction. The score is a
 * transparent ratio of completed pre-exchange milestones to total
 * pre-exchange milestones. Every output (score, state, completed
 * list, outstanding list, top blocker) is **derivable from the
 * deal's stage + activity feed** — operators can always explain
 * the number.
 *
 * Doctrine:
 *   - Operational vocabulary only.
 *   - Never alarm; recede on the way down.
 *   - Calm states: Exchange Nearing · Progressing Well · Awaiting
 *     Enquiries · Awaiting Legal Response · Requires Attention.
 *
 * Mapping (canonical):
 *   Internal 12 stages    → readiness milestones (8 pre-exchange)
 *   offer / memo          → Offer accepted
 *   aml                   → AML complete
 *   solicitors            → Solicitors instructed
 *   searches              → Searches returned
 *   survey                → Survey returned
 *   mortgage_app          → Mortgage applied
 *   enquiries             → Enquiries raised
 *   mortgage_offer        → Mortgage offer received
 *   exchange_ready        → Contracts signed-off / pre-exchange
 *   exchanged | completed → Exchange complete (terminal — score 100)
 */

// Canonical 8 pre-exchange milestones (terminal Exchange = 100%).
// Order mirrors the system-canonical DEAL_STAGES flow in chainStore:
// offer → aml → solicitor → searches → enquiries → mortgage_offer
// → contracts → exchange_date. This ordering ensures that advancing
// the deal stage forward NEVER causes readiness to regress.
export const READINESS_MILESTONES = [
  { id: "offer_accepted",       label: "Offer accepted" },
  { id: "aml_complete",         label: "AML complete" },
  { id: "solicitor_instructed", label: "Solicitors instructed" },
  { id: "searches_returned",    label: "Searches returned" },
  { id: "enquiries_resolved",   label: "Final enquiries resolved" },
  { id: "mortgage_offer",       label: "Mortgage offer received" },
  { id: "contracts_ready",      label: "Draft contracts received" },
  { id: "exchange_date_agreed", label: "Exchange date agreed" },
];

// Stage → highest milestone reached. Anything below this index is
// implied complete, anything above is outstanding. The current
// milestone is in-flight. Aligned with chainStore.DEAL_STAGES order:
// offer → aml → memo → solicitors → mortgage_app → survey → searches
// → enquiries → mortgage_offer → exchange_ready → exchanged → completed
const STAGE_TO_MILESTONE_IDX = {
  offer:          0,
  memo:           0,
  aml:            1,
  solicitors:     2,
  mortgage_app:   2,
  survey:         3,
  searches:       3,
  enquiries:      4,
  mortgage_offer: 5,
  exchange_ready: 7,
  exchanged:      8, // terminal — score 100
  completed:      8,
};

// Blocker sentence per outstanding milestone. Calm, operational —
// never blames a party, never uses alarm words.
const OUTSTANDING_BLOCKER = {
  offer_accepted:       "Offer confirmation pending",
  aml_complete:         "AML verification in progress",
  solicitor_instructed: "Solicitor instruction in flight",
  searches_returned:    "Searches commissioned · awaiting return",
  enquiries_resolved:   "Final enquiries with buyer solicitor",
  mortgage_offer:       "Awaiting mortgage offer",
  contracts_ready:      "Draft contracts awaited",
  exchange_date_agreed: "Exchange date to be agreed",
};

// State derivation — calm, deterministic, explainable.
function deriveState({ score, outstandingIds, isTerminal }) {
  if (isTerminal)             return "Exchanged";
  if (score >= 85)            return "Exchange Nearing";
  if (outstandingIds.includes("enquiries_resolved")) return "Awaiting Enquiries";
  if (outstandingIds.includes("contracts_ready") ||
      outstandingIds.includes("solicitor_instructed"))
                              return "Awaiting Legal Response";
  if (score < 35)             return "Requires Attention";
  return "Progressing Well";
}

// ─── Activity boost ────────────────────────────────────────────────
// Real activity rows can push specific milestones into "complete"
// even if the stage hasn't moved yet (e.g., a `searches_returned`
// activity logged while the deal is still officially in
// `mortgage_app`). This makes the readiness reflect ground truth.
const ACTIVITY_TO_MILESTONE = {
  aml_pack_uploaded:        "aml_complete",
  memorandum_acknowledged:  "solicitor_instructed",
  searches_returned:        "searches_returned",
  mortgage_offer_received:  "mortgage_offer",
  enquiries_acknowledged:   "enquiries_resolved",
  contract_pack_issued:     "contracts_ready",
  management_pack_received: "solicitor_instructed",
};

// Title-keyword fallback — the live `/api/v2/deals/{id}/activity`
// feed returns rows with `activity_type` (generic, e.g.
// `milestone_completed`, `document_upload`) and a descriptive
// `title`. We scan the title for canonical keywords so calm
// operator-logged events surface in the readiness engine.
const TITLE_KEYWORD_TO_MILESTONE = [
  { rx: /\bsearches?\s+returned/i,      id: "searches_returned" },
  { rx: /\bmortgage\s+(offer|issued|approved|received)/i, id: "mortgage_offer" },
  { rx: /\b(final\s+)?enquir(ies|y)\s+(resolved|acknowledged|cleared|answered)/i, id: "enquiries_resolved" },
  { rx: /\bmanagement\s+pack\s+received/i, id: "solicitor_instructed" },
  { rx: /\bmemorandum\s+(of\s+sale\s+)?(issued|acknowledged)/i, id: "solicitor_instructed" },
  { rx: /\baml\b.*?(complete|verified|cleared|pass)/i, id: "aml_complete" },
  { rx: /\b(draft\s+)?contracts?\s+(received|signed|issued|approved)/i, id: "contracts_ready" },
  { rx: /\bexchange\s+date\s+(agreed|confirmed|booked)/i, id: "exchange_date_agreed" },
];

/**
 * Compute readiness for a deal.
 *
 * @param {Object} deal          — deal record with `stage` and optional `activity`.
 * @param {Array}  activity      — optional pre-fetched activity rows.
 * @returns {Object}  readiness  — { score, state, completed[], outstanding[], topBlocker }.
 */
export function computeExchangeReadiness(deal, activity = []) {
  if (!deal) {
    return {
      score: 0,
      state: "Progressing Well",
      completed: [],
      outstanding: READINESS_MILESTONES.map((m) => ({ ...m, blocker: OUTSTANDING_BLOCKER[m.id] })),
      topBlocker: OUTSTANDING_BLOCKER.offer_accepted,
    };
  }

  const stageIdx = STAGE_TO_MILESTONE_IDX[deal.stage] ?? 0;
  const isTerminal = stageIdx >= READINESS_MILESTONES.length;

  // Stage-derived completion baseline — every milestone below the
  // current stage is implicitly done.
  const completedSet = new Set(
    READINESS_MILESTONES.slice(0, Math.min(stageIdx, READINESS_MILESTONES.length))
      .map((m) => m.id)
  );

  // Activity-derived overrides — explicit ground-truth signals.
  for (const row of activity || []) {
    const kind = row?.kind || row?.event || row?.type || row?.activity_type;
    const mappedId = ACTIVITY_TO_MILESTONE[kind];
    if (mappedId) { completedSet.add(mappedId); continue; }
    // Title keyword fallback for the live v2 activity feed.
    const title = row?.title || "";
    for (const { rx, id } of TITLE_KEYWORD_TO_MILESTONE) {
      if (rx.test(title)) { completedSet.add(id); break; }
    }
  }

  const completed = READINESS_MILESTONES
    .filter((m) => completedSet.has(m.id))
    .map((m) => ({ ...m, status: "complete" }));

  const outstanding = READINESS_MILESTONES
    .filter((m) => !completedSet.has(m.id))
    .map((m) => ({
      ...m,
      status: "outstanding",
      blocker: OUTSTANDING_BLOCKER[m.id],
    }));

  const total = READINESS_MILESTONES.length;
  const score = isTerminal
    ? 100
    : Math.min(98, Math.max(8, Math.round((completed.length / total) * 100)));

  const outstandingIds = outstanding.map((m) => m.id);
  const state = deriveState({ score, outstandingIds, isTerminal });

  // Top blocker — the first outstanding milestone (chronological
  // order matches the operational sequence).
  const topBlocker = outstanding.length > 0
    ? outstanding[0].blocker
    : "All operational milestones complete";

  return { score, state, completed, outstanding, topBlocker };
}

// Tone tokens per state — calm violet by default, amber for
// blocker-heavy states, emerald only for the Exchange Nearing /
// Exchanged terminal states.
export const READINESS_STATE_TONE = {
  "Exchange Nearing":       { dot: "#10B981", fg: "#047857", ring: "rgba(16,185,129,0.18)" },
  "Progressing Well":       { dot: "#6366F1", fg: "#4338CA", ring: "rgba(99,102,241,0.18)" },
  "Awaiting Enquiries":     { dot: "#F59E0B", fg: "#B45309", ring: "rgba(245,158,11,0.18)" },
  "Awaiting Legal Response": { dot: "#F59E0B", fg: "#B45309", ring: "rgba(245,158,11,0.18)" },
  "Requires Attention":     { dot: "#EF4444", fg: "#B91C1C", ring: "rgba(239,68,68,0.18)" },
  "Exchanged":              { dot: "#10B981", fg: "#047857", ring: "rgba(16,185,129,0.20)" },
};
